import { Card } from '@ipfabric/ui-components/Card/Card';
import type { ApiTablePageComponent } from 'components/ApiTablePage/ApiTablePageComponent';
import { ButtonLink } from 'components/Button/ButtonLink';
import { CloudLicenseSection } from 'components/LicenseConsumptionOverview/CloudLicenseSection';
import { ConstructBreakdownPanel } from 'components/LicenseConsumptionOverview/ConstructBreakdownPanel';
import { DeviceBreakdownPanel } from 'components/LicenseConsumptionOverview/DeviceBreakdownPanel';
import { LicenseSectionsGrid } from 'components/LicenseConsumptionOverview/LicenseSectionsGrid';
import { OnPremLicenseSection } from 'components/LicenseConsumptionOverview/OnPremLicenseSection';
import { useLicenseConsumptionOverview } from 'components/LicenseConsumptionOverview/useLicenseConsumptionOverview';
import { useCurrentSnapshotLicenseUsage } from 'pages/SnapshotManagement/useCurrentSnapshotLicenseUsage';
import { renderQuery } from 'utils/react-query/renderQuery';

/**
 * Overview tab of the License overview page.
 *
 * Deliberately does not render the OAS table of
 * `/tables/settings/administration/license-consumption/overview` - the tab shows the license consumption summary
 * for on-premise devices and cloud constructs, not a table, so `renderApiTablePage` is intentionally
 * unused. Both breakdowns come from that endpoint through a single request, split by category.
 *
 * The summary bars are driven by the snapshot alone, so they render while the breakdowns load.
 */
export const LicenseConsumptionOverviewApiTablePage: ApiTablePageComponent =
  () => {
    const licenseUsage = useCurrentSnapshotLicenseUsage();
    const overviewQuery = useLicenseConsumptionOverview();

    // The page is reachable only for snapshots that carry license usage; without it there is nothing
    // to summarise, and a half-filled card would read as broken data.
    if (licenseUsage === undefined) {
      return null;
    }

    return (
      <Card className='flex flex-1 flex-col gap-lg p'>
        <div className='flex items-center justify-between gap'>
          <div className='font-semibold text-default text-level-3'>
            License consumption overview
          </div>
          <ButtonLink to='/upload-license' variant='secondary' icon='fa-upload'>
            Upload license file
          </ButtonLink>
        </div>
        <LicenseSectionsGrid>
          <OnPremLicenseSection
            className='pr-2xl'
            onPremise={licenseUsage.onPremise}
          >
            {renderQuery({
              query: overviewQuery,
              renderStaleDataOnMount: true,
              success: ({ data }) => <DeviceBreakdownPanel rows={data} />,
            })}
          </OnPremLicenseSection>
          <CloudLicenseSection
            className='border-divider border-l border-solid pl-2xl'
            cloud={licenseUsage.cloud}
          >
            {renderQuery({
              query: overviewQuery,
              renderStaleDataOnMount: true,
              success: ({ data }) => <ConstructBreakdownPanel rows={data} />,
            })}
          </CloudLicenseSection>
        </LicenseSectionsGrid>
      </Card>
    );
  };
